import _ from 'lodash'
import XLSX from 'xlsx'
import ReservationByDay, {
  BoarderSlotSelection,
  MenuGroupSlotWithCount,
  MenuGroupWithSlots,
  SlotSelection
} from '@/model/reporting/ReservationByDay'
import { ReservationStatus } from '@/types/enums'
import Customer from '@/model/customer/Customer'
import BoarderMonthlyReservationStatusByDay from '@/model/reporting/BoarderMonthlyReservationStatusByDay'
import moment from 'moment'

/**
 * Vytvorí dátovú štruktúru pre data grid prehľadu objednávok.
 *
 * Názvy skupín sú indexované podľa poradia, nie podľa ID.
 *
 * Príklad štruktúry:
 * {
 *    'customerName': 'SGYMLC',
 *    'boarderGroupName': 'prima',
 *
 *    'menuGroup-1': {
 *      'selectionACount': 10
 *    },
 *
 *    'menuGroup-2': {
 *      'selectionACount': 8,
 *      'selectionBCount': 2
 *    },
 *
 *    'canceledCount': 1,
 *
 *    'boarderList': [
 *      { 'boarderName': 'Stravník 1', 'selectionGroup1': 'A', 'selectionGroup2': 'A', 'isCanceled': false },
 *      { 'boarderName': 'Stravník 2', 'selectionGroup1': 'A', 'selectionGroup2': 'B', 'isCanceled': false },
 *      { 'boarderName': 'Stravník 3', 'selectionGroup1': 'A', 'selectionGroup2': 'A', 'isCanceled': true },
 *      { 'boarderName': 'Stravník 4', 'selectionGroup1': 'A', 'selectionGroup2': 'B', 'isCanceled': false },
 *      { 'boarderName': 'Stravník 5', 'selectionGroup1': 'A', 'selectionGroup2': 'A', 'isCanceled': false }
 *    ]
 * }
 *
 * @param data
 */
export function createDataForGrid (data: ReservationByDay[]): any[] {
  return data.map((item: ReservationByDay) => (
    {
      customerName: item.customer.name,
      boarderGroupName: item.boarderGroup.name,
      ...createMenuGroups(item.menuGroupList),
      canceledCount: item.canceledCount,
      boarderList: createBoarderList(item.boarderList)
    }
  ))
}

/**
 * Spočíta celkové počty pre jednotlivé sloty a vráti ich ako riadok pre summaryData.
 *
 * Príklad štruktúry:
 * {
 *   'menuGroup-1': {
 *     'selectionACount': 11
 *   },
 *
 *   'menuGroup-2': {
 *     'selectionACount': 20,
 *     'selectionBCount': 20
 *   },
 *
 *   'canceledCount': 10,
 * }
 *
 * @param data
 */
export function createSummaryDataForGrid (data: ReservationByDay[]): any[] {
  const out: any = {
    ...calculateSlotSummary(data),

    canceledCount: _.chain(data)
      .map((d: ReservationByDay) => d.canceledCount)
      .sum()
      .value()
  }

  return [out]
}

function createMenuGroups (list: MenuGroupWithSlots[]): object {
  let out: any = {}

  list.forEach((mg: MenuGroupWithSlots, idx: number) => {
    const key = `menuGroup-${idx + 1}`
    let values: any = {}

    mg.slotList.forEach((mgs: MenuGroupSlotWithCount) => {
      values[`selection${mgs.menuGroupSlot.code}Count`] = mgs.count
    })

    out[key] = values
  })

  return out
}

function calculateSlotSummary (data: ReservationByDay[]): object {
  let out: any = {}

  data.forEach((item: ReservationByDay) => {
    item.menuGroupList.forEach((mg: MenuGroupWithSlots, idx: number) => {
      const groupKey = `menuGroup-${idx + 1}`

      if (!out[groupKey]) {
        out[groupKey] = {}
      }

      mg.slotList.forEach((mgs: MenuGroupSlotWithCount) => {
        const slotKey = `selection${mgs.menuGroupSlot.code}Count`

        if (!out[groupKey][slotKey]) {
          out[groupKey][slotKey] = 0
        }

        out[groupKey][slotKey] += mgs.count
      })
    })
  })

  return out
}

function createBoarderList (list: BoarderSlotSelection[]): any[] {
  return list.map((i: BoarderSlotSelection) => ({
    boarderName: i.boarder.person.fullName,
    ...createBoarderSelection(i)
  }))
}

function createBoarderSelection (selection: BoarderSlotSelection): object {
  let out: any = {}

  selection.slotSelectionList.forEach((i: SlotSelection, idx: number) => {
    const key = `selectionGroup${idx + 1}`
    const value = i.selectedSlot ? i.selectedSlot.code : '-'

    out.isCanceled = i.reservationStatus === ReservationStatus.canceled

    out[key] = value
  })

  return out
}
